import { createElement, useLayoutEffect } from 'react'
import { reviewPresentationClassName, reviewPresentationDataAttributes } from '@w/content-blocks'
import type { BlockView } from '../view-api'
import { blockElement, useReview } from './context'

export const PENDING_COMMENT_ID_PREFIX = 'review-comment-draft:'
export const COMMENT_REF_HIGHLIGHT_CLASSES = [
  'bg-surface-warning', '[&_.cb-inline-code]:bg-surface-warning',
  'decoration-warning', 'rounded-sm', 'underline', 'decoration-2', 'underline-offset-2',
] as const

export const ReviewView: BlockView = ({ block, api, children }) => {
  const structural = block.type === 'review' && block.children.every(child => ['li', 'tr', 'td'].includes(child.type))
  useLayoutEffect(() => {
    if (!structural || block.type !== 'review' || api.mode !== 'review') return () => {}
    const classes = reviewPresentationClassName(block).split(' ').filter(Boolean)
    const elements = block.children.flatMap(child => blockElement(api.root, child.id) ?? [])
    for (const element of elements) {
      element.dataset.reviewId = block.id
      element.dataset.coreReview = 'true'
      element.dataset.reviewChange = block.change
      element.dataset.reviewDisplay = block.display
      element.dataset.reviewState = block.state
      element.classList.add(...classes)
    }
    return () => {
      for (const element of elements) {
        element.classList.remove(...classes)
        for (const key of ['data-review-id', 'data-core-review', 'data-review-change', 'data-review-display', 'data-review-state']) element.removeAttribute(key)
      }
    }
  }, [api.root, api.mode, block, structural])
  if (block.type !== 'review' || api.mode !== 'review' || structural) return children
  return createElement(block.display === 'inline' ? 'span' : 'div', {
    'data-block-id': block.id, 'data-review-id': block.id, 'data-core-review': 'true',
    ...reviewPresentationDataAttributes(block), className: reviewPresentationClassName(block),
  }, children)
}

export function diffClass(kind: string, inline = true) {
  return kind === 'add' ? `bg-surface-success no-underline${inline ? ' text-on-surface-success rounded-sm [[data-diff-kind=delete]+&]:rounded-l-none' : ''}`
    : kind === 'delete' ? `bg-surface-error no-underline${inline ? ' text-on-surface-error rounded-sm has-[+[data-diff-kind=add]]:rounded-r-none' : ''}` : ''
}
export const DiffView: BlockView = ({ block, api, children }) => {
  if (block.type !== 'diff' || api.mode !== 'review') return children
  return <span data-block-id={block.id} data-diff-kind={block.kind} className={diffClass(block.kind)}>{children}</span>
}
export const CommentView: BlockView = ({ block, children }) => {
  const { comments } = useReview()
  return block.type === 'comment'
    ? <mark data-block-id={block.id} data-core-comment-ref="true" data-comment-id={block.commentId}
        data-review-comment-thread-known={comments?.some(thread => thread.threadId === block.commentId) ? 'true' : undefined}
        data-testid={`content-blocks-editor-review-comment-ref-${block.id}`} className="text-inherit hover:text-warning focus:text-warning data-[review-comment-active]:text-warning">{children}</mark> : children
}
export const CommentsView: BlockView = () => null
